import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { candidateAPI, authAPI } from '../services/api';
import { positionsForRole } from '../utils/roles';
import Navigation from '../components/Navigation';
import './CreateCandidate.css';

function CreateCandidate({ onLogout, onFeedback }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  // Positions this user's role may create. Starts as the full list and is
  // narrowed once we know the user's role (the backend enforces this too).
  const [allowedPositions, setAllowedPositions] = useState(positionsForRole('admin'));
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    source: 'website',
    position_applied: 'receptionist',
    employment_type: 'PERMANENT',
    temporary_reason: '',
  });

  useEffect(() => {
    let cancelled = false;
    authAPI.me().then((me) => {
      if (cancelled) return;
      const positions = positionsForRole(me.role);
      setAllowedPositions(positions);
      // Ensure the selected position is one the user can actually create.
      setFormData((prev) =>
        positions.some((p) => p.value === prev.position_applied)
          ? prev
          : { ...prev, position_applied: positions.length ? positions[0].value : '' }
      );
    }).catch(() => {/* fall back to full list; backend still enforces */});
    return () => { cancelled = true; };
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const candidate = await candidateAPI.create(formData);
      // Navigate immediately - no blocking alert needed
      navigate(`/candidates/${candidate.id}`);
    } catch (error) {
      console.error('Error creating candidate:', error);
      // Errors still require user acknowledgment
      alert(error.response?.data?.detail || 'Failed to create candidate');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Navigation onLogout={onLogout} onFeedback={onFeedback} />
      <div className="container">
        <h1>Add New Candidate</h1>

        <form onSubmit={handleSubmit} className="candidate-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="first_name">First Name *</label>
              <input
                type="text"
                id="first_name"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                required
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="last_name">Last Name *</label>
              <input
                type="text"
                id="last_name"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                required
                disabled={loading}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="email">Email *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="phone">Phone</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                disabled={loading}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="source">Source *</label>
              <select
                id="source"
                name="source"
                value={formData.source}
                onChange={handleChange}
                required
                disabled={loading}
              >
                <option value="website">Website</option>
                <option value="indeed">Indeed</option>
                <option value="word_of_mouth">Word of Mouth</option>
                <option value="writtle_college">Writtle College</option>
                <option value="see_practice">See Practice</option>
                <option value="recruiter">Recruiter</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="position_applied">Position Applied *</label>
              <select
                id="position_applied"
                name="position_applied"
                value={formData.position_applied}
                onChange={handleChange}
                required
                disabled={loading}
              >
                {allowedPositions.map((p) => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="employment_type">Employment Type *</label>
              <select
                id="employment_type"
                name="employment_type"
                value={formData.employment_type}
                onChange={handleChange}
                required
                disabled={loading}
              >
                <option value="PERMANENT">Permanent</option>
                <option value="TEMPORARY">Temporary</option>
              </select>
            </div>

            {formData.employment_type === 'TEMPORARY' && (
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label htmlFor="temporary_reason">Reason for the temporary contract</label>
                <textarea
                  id="temporary_reason"
                  name="temporary_reason"
                  rows={2}
                  value={formData.temporary_reason}
                  onChange={handleChange}
                  placeholder="e.g. maternity cover for ..."
                  disabled={loading}
                  style={{ width: '100%', boxSizing: 'border-box', fontFamily: 'inherit' }}
                />
              </div>
            )}
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => navigate('/candidates')}
              disabled={loading}
            >
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Creating...' : 'Create Candidate'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CreateCandidate;
